import { useCallback, type PropsWithChildren } from "react";

import { useSelector } from "react-redux";

import leadsSearchTrackingService from "app/pages/sales-intelligence/modules/leads-search/services/tracking/trackingService";
import { getLeadDomainFromRow } from "app/pages/sales-intelligence/sub-modules/company-search/helpers/rows";
import { constructHref } from "app/pages/sales-intelligence/utils/url";
import { selectRightBarIsOpen } from "app/pages/workspace/sales/sub-modules/common/store/selectors";
import { GrowthbookService, TestsNames } from "app/services/trackingServices/GrowthBookService";

import LeadCell from "../../../components/cells/LeadCell/LeadCell";
import { createLeadOverviewHref } from "../../../configuration/columns/utils";
import { type HookParams, useLeadCellActions } from "../../../hooks/cells/useLeadCellActions";
import type { LeadsType } from "../../../types";

type Props = PropsWithChildren<
    HookParams & {
        leadsType: LeadsType;
        withWebsiteLink?: boolean;
    }
>;

const LeadCellContainer = ({ leadsType, children, record, onToggleRightSidebar, withWebsiteLink = true }: Props) => {
    const { onToggleSidebar, isSidebarDisabled } = useLeadCellActions({
        record,
        onToggleRightSidebar,
    });

    const domain = getLeadDomainFromRow(record);
    const websiteLink = withWebsiteLink ? constructHref(domain) : undefined;
    const isRightSidebarOpen = useSelector(selectRightBarIsOpen);
    const isSidebarABTest = GrowthbookService.growthbookInstance.isOn(TestsNames.SI_SIDEBAR_ICON);

    const onToggleRightSidebarWithTracking = useCallback(() => {
        leadsSearchTrackingService.trackSidebarToggle(isRightSidebarOpen);
        onToggleSidebar();
    }, [isRightSidebarOpen, onToggleSidebar]);

    const handleClick = useCallback(() => {
        if (isSidebarABTest) {
            onToggleRightSidebarWithTracking();
        } else {
            leadsSearchTrackingService.trackCompanyCellClick(record.company ?? "");
        }
    }, [isSidebarABTest, onToggleRightSidebarWithTracking, record.company]);

    return (
        <LeadCell
            leadsType={leadsType}
            iconSrc={record.favicon}
            overviewLink={isSidebarABTest ? undefined : createLeadOverviewHref(record)}
            withNewBadge={record.is_new}
            onToggleRightSidebar={onToggleRightSidebarWithTracking}
            websiteLink={websiteLink}
            isSidebarDisabled={isSidebarDisabled}
            onClick={handleClick}
            isSidebarABTest={isSidebarABTest}
        >
            {children}
        </LeadCell>
    );
};

export default LeadCellContainer;
